/**
 * Расчёт модели по годам — для панели модели и печатной формы: статьи и итоги
 * по годам, промежуточные величины листа «Расчет машино-час».
 */
import styled from 'styled-components';
import type { PrcModelResult, PrcYearResult } from '@shared/api';
import { Matrix, OIL_KINDS, ScrollRegion, VisuallyHidden, fleetType } from '@entities/fleet';
import { PRC_COST_ARTICLES, PRC_TOTAL_ARTICLES, isTotalArticle } from '@entities/prc';
import { formatNumber, formatServerMoney } from '../format';

const Table = styled(Matrix)`
  tbody th.total,
  tbody td.total {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  tbody tr.split > th,
  tbody tr.split > td {
    border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
  }

  caption {
    font-size: ${fleetType.small};
  }
`;

/** Разовые величины — две колонки: не растягиваем на всю ширину, подпись и число рядом. */
const KvTable = styled(Table)`
  width: auto;
  min-width: min(560px, 100%);
`;

type YearKey = keyof PrcYearResult;

function yearValue(result: PrcModelResult, year: number, key: YearKey): unknown {
  return result.years[String(year)]?.[key];
}

/** Статьи и итоги по годам горизонта, ₽ на маш.-час. */
export function ArticlesByYear({
  result,
  years,
  label = 'Статьи по годам',
}: {
  result: PrcModelResult;
  years: readonly number[];
  label?: string;
}) {
  const rows = [...PRC_COST_ARTICLES, ...PRC_TOTAL_ARTICLES];
  return (
    <ScrollRegion label={label}>
      <Table>
        <caption>
          <VisuallyHidden>{label}, </VisuallyHidden>₽ на маш.-час; цена с НДС — с налогом, остальное
          без
        </caption>
        <thead>
          <tr>
            <th scope="col">Статья</th>
            {years.map((y) => (
              <th key={y} scope="col">
                {y}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            const total = isTotalArticle(a.code);
            return (
              <tr key={a.code} className={a.code === 'cost' ? 'split' : undefined}>
                <th scope="row" className={total ? 'total' : undefined}>
                  {a.label}
                </th>
                {years.map((y) => (
                  <td key={y} className={total ? 'total' : undefined}>
                    {formatServerMoney(yearValue(result, y, a.code), false)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </ScrollRegion>
  );
}

const ONE_OFF: readonly { key: keyof PrcModelResult; label: string; unit: string; max: number }[] =
  [
    { key: 'annual_amortization', label: 'Годовая амортизация', unit: '₽', max: 2 },
    { key: 'monthly_amortization', label: 'Месячная амортизация', unit: '₽', max: 2 },
    { key: 'years_to_writeoff', label: 'Лет до полного восстановления', unit: 'лет', max: 2 },
    { key: 'annual_hours', label: 'Годовой режим с коэффициентом зоны', unit: 'маш.-ч', max: 2 },
    { key: 'repair_norm_pct', label: 'Норма затрат на ремонт и ТО', unit: '%', max: 4 },
    { key: 'tire_norm_mileage_km', label: 'Норма пробега шины', unit: 'км', max: 0 },
    { key: 'tire_usage', label: 'Использование комплекта шин за год', unit: '', max: 4 },
    { key: 'fuel_annual_l', label: 'Топливо в год', unit: 'л', max: 2 },
    { key: 'fuel_monthly_l', label: 'Топливо в месяц', unit: 'л', max: 2 },
    { key: 'fuel_hourly_l', label: 'Топливо в час с оборудованием', unit: 'л', max: 4 },
    { key: 'hydraulic_annual', label: 'Гидрожидкость в год', unit: '', max: 4 },
    { key: 'hydraulic_monthly', label: 'Гидрожидкость в месяц', unit: '', max: 4 },
    { key: 'hydraulic_hourly', label: 'Гидрожидкость в час', unit: '', max: 6 },
    { key: 'coolant_hourly_l', label: 'Охлаждающая жидкость в час', unit: 'л', max: 6 },
  ];

const PER_YEAR: readonly { key: YearKey; label: string }[] = [
  { key: 'repair_annual', label: 'Затраты на ремонт в год, ₽' },
  { key: 'tire_set', label: 'Комплект шин, ₽' },
  { key: 'tire_set_delivered', label: 'Комплект с доставкой и заменой, ₽' },
];

/** Промежуточные величины: разовые и по годам (смазочные — по видам). */
export function IntermediateValues({
  result,
  years,
}: {
  result: PrcModelResult;
  years: readonly number[];
}) {
  const kinds = OIL_KINDS.filter((k) =>
    years.some((y) => result.years[String(y)]?.lubricants_annual?.[k.key] !== undefined),
  );
  return (
    <>
      <ScrollRegion label="Промежуточные величины">
        <KvTable>
          <caption>
            <VisuallyHidden>Промежуточные величины</VisuallyHidden>
          </caption>
          <tbody>
            {ONE_OFF.map((f) => {
              const raw = result[f.key];
              if (raw === null || raw === undefined || typeof raw === 'object') return null;
              return (
                <tr key={f.key}>
                  <th scope="row">
                    {f.label}
                    {f.unit ? `, ${f.unit}` : ''}
                  </th>
                  <td>{formatNumber(raw, f.max)}</td>
                </tr>
              );
            })}
          </tbody>
        </KvTable>
      </ScrollRegion>
      <ScrollRegion label="Промежуточные величины по годам">
        <Table>
          <caption>По годам</caption>
          <thead>
            <tr>
              <th scope="col">Величина</th>
              {years.map((y) => (
                <th key={y} scope="col">
                  {y}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PER_YEAR.map((f) => (
              <tr key={f.key}>
                <th scope="row">{f.label}</th>
                {years.map((y) => (
                  <td key={y}>{formatNumber(yearValue(result, y, f.key), 2, 2)}</td>
                ))}
              </tr>
            ))}
            {kinds.map((k) => (
              <tr key={k.key}>
                <th scope="row">Смазочные в год: {k.label.toLowerCase()}</th>
                {years.map((y) => (
                  <td key={y}>
                    {formatNumber(result.years[String(y)]?.lubricants_annual?.[k.key], 3)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollRegion>
    </>
  );
}
